<script setup lang="ts">
import { useBreadcrumbSchema } from "~/composables/useBreadcrumbSchema";
import { BreadcrumbRoute } from "~/types/breadcrumb-routes";

const url = useRequestURL();
const { data, error } = await useFetch(
  `${url.origin}/content/services/mobility/flutter-app-development-content.json`,
  { default: () => ({}) }
);
if (error.value) console.error("[flutter-page] content fetch failed:", error.value.status);

interface FlutterMetaTag {
  name?: string;
  property?: string;
  content: string;
}
interface FlutterCardItem {
  title: string;
  description: string;
  svgIcon?: string;
}
interface FlutterPageContent {
  meta_tags: {
    title: string;
    canonical?: string;
    meta: FlutterMetaTag[];
  };
  hero: { heading: string; subcopy: string };
  services: {
    heading: string;
    subcopy: string;
    ctaText?: string;
    items: FlutterCardItem[];
  };
  whyFlutter: {
    heading: string;
    subtitle: string;
    leftTitle: string;
    rightTitle: string;
    ctaText?: string;
    leftCards: FlutterCardItem[];
    rightCards: FlutterCardItem[];
  };
  process: {
    heading: string;
    subcopy: string;
    ctaText?: string;
    steps: { title: string; description: string }[];
  };
  techStack: {
    heading: string;
    subtitle: string;
    items: FlutterCardItem[];
  };
  useCases: {
    heading: string;
    subcopy: string;
    ctaText?: string;
    items: FlutterCardItem[];
  };
  whyUs: {
    heading: string;
    subcopy: string;
    items: FlutterCardItem[];
  };
  faq: {
    heading: string;
    subcopy: string;
    ctaText?: string;
    items: { title: string; description: string }[];
  };
  cta: {
    heading: string;
    subcopy: string;
    ctaText?: string;
    items: FlutterCardItem[];
  };
}
const content = data.value as FlutterPageContent;

const description =
  (content?.meta_tags?.meta as any[]).find((m) => m.name === "description")
    ?.content ||
  "WebReinvent builds cross-platform Flutter apps for Android and iOS with one codebase — faster releases, native performance, and a team that has shipped 150+ apps. Book a call.";

/* ─── SEO ─── */
const breadcrumbs = useBreadcrumbSchema(BreadcrumbRoute.Services, content?.hero?.heading);

const pageTitle =
  content?.meta_tags?.title || "Flutter App Development - Build Faster | WebReinvent";

useHead({
  title: pageTitle,
  meta: [
    ...((content?.meta_tags?.meta || []) as any[]),
    { name: "description", content: description },
    { property: "og:title", content: pageTitle },
    { property: "og:description", content: description },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: pageTitle },
    { name: "twitter:description", content: description },
  ],
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Service",
            name: content?.hero?.heading || "Flutter App Development",
            serviceType: "Mobile App Development",
            image: `${url.origin}/images/services/flutter-logo.webp`,
            url: `${url.origin}/services/mobility/flutter-app-development`,
            description: content?.hero?.subcopy || "Flutter app development services for Android and iOS cross-platform mobile applications.",
            provider: {
              "@type": "Organization",
              name: "WebReinvent",
              url: "https://webreinvent.com",
              sameAs: [
                "https://linkedin.com/company/webreinvent",
                "https://instagram.com/webreinvent",
                "https://facebook.com/webreinvent",
              ],
            },
            areaServed: {
              "@type": "Country",
              name: "India",
            },
          },
          {
            "@type": "FAQPage",
            mainEntity: (content?.faq?.items || []).map((item: { title: string; description: string }) => ({
              "@type": "Question",
              name: item.title,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.description.replace(/<[^>]*>/g, ""),
              },
            })),
          },
          ...(breadcrumbs.value ? [breadcrumbs.value] : []),
        ],
      }),
    },
  ],
});

const WrFlutterHeroSection = defineAsyncComponent(() => import('~/components/molecules/WrFlutterHeroSection.vue'));
const WrFlutterServicesSection = defineAsyncComponent(() => import('~/components/molecules/WrFlutterServicesSection.vue'));
const WrFlutterWhyFlutterSection = defineAsyncComponent(() => import('~/components/molecules/WrFlutterWhyFlutterSection.vue'));
const WrFlutterProcessSection = defineAsyncComponent(() => import('~/components/molecules/WrFlutterProcessSection.vue'));
const WrFlutterTechStackSection = defineAsyncComponent(() => import('~/components/molecules/WrFlutterTechStackSection.vue'));
const WrFlutterUseCasesSection = defineAsyncComponent(() => import('~/components/molecules/WrFlutterUseCasesSection.vue'));
const WrFlutterWhyUsSection = defineAsyncComponent(() => import('~/components/molecules/WrFlutterWhyUsSection.vue'));
const WrFlutterFaqSection = defineAsyncComponent(() => import('~/components/molecules/WrFlutterFaqSection.vue'));
const WrFlutterCtaSection = defineAsyncComponent(() => import('~/components/molecules/WrFlutterCtaSection.vue'));
const WrFlutterRelatedSection = defineAsyncComponent(() => import('~/components/molecules/WrFlutterRelatedSection.vue'));
const WrFlutterMaskSection = defineAsyncComponent(() => import('~/components/molecules/WrFlutterMaskSection.vue'));
const WrTestimonialCardCarousel = defineAsyncComponent(() => import('~/components/molecules/WrTestimonialCardCarousel.vue'));

const visible = ref(false);

const openModal = () => {
  visible.value = true;
};

const colorMode = useColorMode();
const isDark = computed(() => colorMode.value === "dark");

/* This page renders its own "webreinvent" section (WrFlutterMaskSection)
   on the page background, so hide the layout's shared #mask-section while
   this page is active. onActivated/onDeactivated cover Nuxt's keep-alive
   page caching, so the class can't leak to other service pages. */
const setFlutterPage = (on: boolean) => {
  document.documentElement.classList.toggle("flutter-page", on);
};
onMounted(() => setFlutterPage(true));
onActivated(() => setFlutterPage(true));
onDeactivated(() => setFlutterPage(false));
onBeforeUnmount(() => setFlutterPage(false));

definePageMeta({
  layout: "services-layout",
});
</script>

<template>
  <main class="min-h-screen px-4 lg:px-0" :class="isDark ? 'main-bg-gradient' : 'main-bg-gradient-light'">
    <div class="max-w-screen-xl mx-auto">
    <!-- Figma background: base gradient (109.63deg) + edge vignette overlay (dark theme only) -->
    <!-- Ambient streaks + bottom glows (from WrFlutterHeroSection's removed bg layers), fixed behind the whole page.
         The base gradient + vignette live in .main-bg-gradient's background-image (single owner). -->
    <div v-if="isDark" class="flutter-glow-top" />
    <div v-if="isDark" class="flutter-glow-streaks">
      <div class="flutter-streak" style="--w:312.6px;--l:calc(50% - 196px);--t:-370px;--g:35,27,255;--o:0.35;--b:10px;--op:0.38"/>
      <div class="flutter-streak" style="--w:50.2px;--l:calc(50% + 229px);--t:-327px;--g:63,56,255;--o:0.28;--b:20px;--op:0.84"/>
      <div class="flutter-streak" style="--w:146.6px;--l:calc(50% + 90px);--t:-403px;--g:161,37,243;--o:0.71;--b:20px;--op:0.74"/>
      <div class="flutter-streak" style="--w:32.97px;--l:calc(50% - 136px);--t:-327px;--g:161,37,243;--o:0.71;--b:20px"/>
      <div class="flutter-streak" style="--w:85.07px;--l:calc(50% - 287px);--t:-279px;--g:161,37,243;--o:0.71;--b:20px;--op:0.7"/>
    </div>
    <!-- Rising particles; the drifting orbs are part of .main-bg-gradient / -light -->
    <div class="flutter-bg-live" aria-hidden="true">
      <div class="fl-particle" style="--x:8%;--s:3px;--o:0.5;--d:26s;--delay:-4s" />
      <div class="fl-particle" style="--x:18%;--s:2px;--o:0.4;--d:32s;--delay:-12s" />
      <div class="fl-particle" style="--x:27%;--s:4px;--o:0.35;--d:38s;--delay:-20s" />
      <div class="fl-particle" style="--x:38%;--s:2px;--o:0.45;--d:24s;--delay:-8s" />
      <div class="fl-particle" style="--x:47%;--s:3px;--o:0.3;--d:40s;--delay:-26s" />
      <div class="fl-particle" style="--x:56%;--s:2px;--o:0.5;--d:28s;--delay:-16s" />
      <div class="fl-particle" style="--x:64%;--s:4px;--o:0.3;--d:36s;--delay:-2s" />
      <div class="fl-particle" style="--x:72%;--s:2px;--o:0.4;--d:22s;--delay:-10s" />
      <div class="fl-particle" style="--x:81%;--s:3px;--o:0.45;--d:34s;--delay:-22s" />
      <div class="fl-particle" style="--x:88%;--s:2px;--o:0.35;--d:27s;--delay:-6s" />
      <div class="fl-particle" style="--x:94%;--s:3px;--o:0.4;--d:30s;--delay:-14s" />
      <div class="fl-particle" style="--x:13%;--s:2px;--o:0.3;--d:31s;--delay:-18s" />
    </div>

    <div class="relative z-10">
      <!-- Hero Section -->
      <WrFlutterHeroSection :cta-link="null" @cta-clicked="openModal" />

      <div class="flutter-content">
      <!-- What we build -->
      <WrFlutterServicesSection
        v-if="content?.services?.items?.length"
        :heading="content?.services?.heading"
        :subcopy="content?.services?.subcopy"
        :items="content?.services?.items"
        :cta-text="content?.services?.ctaText"
        :cta-link="null"
        @cta-clicked="openModal"
      />

      <!-- Why Flutter -->
      <WrFlutterWhyFlutterSection
        v-if="content?.whyFlutter?.leftCards?.length && content?.whyFlutter?.rightCards?.length"
        :heading="content?.whyFlutter?.heading"
        :subtitle="content?.whyFlutter?.subtitle"
        :left-title="content?.whyFlutter?.leftTitle"
        :left-cards="content?.whyFlutter?.leftCards"
        :right-title="content?.whyFlutter?.rightTitle"
        :right-cards="content?.whyFlutter?.rightCards"
        :cta-text="content?.whyFlutter?.ctaText"
        :cta-link="null"
        @cta-clicked="openModal"
      />

      <!-- How we work -->
      <WrFlutterProcessSection
        v-if="content?.process?.steps?.length"
        :heading="content?.process?.heading"
        :subcopy="content?.process?.subcopy"
        :steps="content?.process?.steps"
        :cta-text="content?.process?.ctaText"
        :cta-link="null"
        @cta-clicked="openModal"
      />

      <!-- Tech stack -->
      <WrFlutterTechStackSection
        v-if="content?.techStack?.items?.length"
        :heading="content?.techStack?.heading"
        :subtitle="content?.techStack?.subtitle"
        :items="content?.techStack?.items"
      />

      <!-- Use cases -->
      <WrFlutterUseCasesSection
        v-if="content?.useCases?.items?.length"
        :heading="content?.useCases?.heading"
        :subcopy="content?.useCases?.subcopy"
        :items="content?.useCases?.items"
        :cta-text="content?.useCases?.ctaText"
        :cta-link="null"
        @cta-clicked="openModal"
      />

      <!-- Why us -->
      <WrFlutterWhyUsSection
        v-if="content?.whyUs?.items?.length"
        :heading="content?.whyUs?.heading"
        :subcopy="content?.whyUs?.subcopy"
        :items="content?.whyUs?.items"
      />

      <!-- Testimonials (shared carousel, same as the other service pages) -->
      <WrTestimonialCardCarousel />

      <!-- FAQ -->
      <WrFlutterFaqSection
        v-if="content?.faq?.items?.length"
        :heading="content?.faq?.heading"
        :subcopy="content?.faq?.subcopy"
        :items="content?.faq?.items"
        :cta-text="content?.faq?.ctaText"
        :cta-link="null"
        @cta-clicked="openModal"
      />

      <!-- CTA -->
      <WrFlutterCtaSection
        v-if="content?.cta?.items?.length"
        :heading="content?.cta?.heading"
        :subcopy="content?.cta?.subcopy"
        :items="content?.cta?.items"
        :cta-text="content?.cta?.ctaText"
        :cta-link="null"
        @cta-clicked="openModal"
      />

      <!-- Related capabilities -->
      <WrFlutterRelatedSection />

      <WrContactFormPopup
        :is-open="visible"
        @close="visible = false"
        class="z-30"
      />
      </div>
    </div>
  </div>
  <!-- Our own "webreinvent" section on the page background; the
       layout's #mask-section is hidden while this page is active -->
  <WrFlutterMaskSection />
  </main>
</template>

<style scoped>
/* Base Figma gradient + three soft color orbs baked into the same
   background. The orbs drift via background-position (slow pan). */
.main-bg-gradient{
  background-image:
    radial-gradient(420px 420px at 10% 6%, rgba(18, 219, 255, 0.10), transparent 70%),
    radial-gradient(520px 520px at 90% 42%, rgba(186, 85, 254, 0.12), transparent 70%),
    radial-gradient(380px 380px at 28% 80%, rgba(35, 27, 255, 0.10), transparent 70%),
    linear-gradient(90deg, #000000 -3.94%, rgba(6, 1, 46, 0) 51.53%, #000000 109.81%),
    linear-gradient(109.63deg, #030226 3.67%, #06143D 83.91%);
  background-repeat: no-repeat;
  animation: fl-bg-pan 40s ease-in-out infinite alternate;
}
@keyframes fl-bg-pan {
  0%   { background-position: 0 0, 0 0, 0 0, 0 0, 0 0; }
  50%  { background-position: 70px 50px, -90px -60px, 60px -70px, 0 0, 0 0; }
  100% { background-position: -50px 90px, 80px -110px, -80px 50px, 0 0, 0 0; }
}
/* Mobile: the Figma edge vignette covers most of a narrow screen, making the
   hero look like a separate black zone. Soften it (translucent black, wider
   clear middle) so the edges blend into the gradient. */
@media (max-width: 640px) {
  .main-bg-gradient {
    background-image:
      radial-gradient(420px 420px at 10% 6%, rgba(18, 219, 255, 0.10), transparent 70%),
      radial-gradient(520px 520px at 90% 42%, rgba(186, 85, 254, 0.12), transparent 70%),
      radial-gradient(380px 380px at 28% 80%, rgba(35, 27, 255, 0.10), transparent 70%),
      linear-gradient(90deg, rgba(0, 0, 0, 0.4) -3.94%, rgba(6, 1, 46, 0) 34%, rgba(6, 1, 46, 0) 66%, rgba(0, 0, 0, 0.4) 109.81%),
      linear-gradient(109.63deg, #030226 3.67%, #06143D 83.91%);
  }
}
/* Light theme: same drifting orbs as soft tints over the light gray base
   (layout background shows through the transparent parts). */
.main-bg-gradient-light{
  background-image:
    radial-gradient(460px 460px at 10% 6%, rgba(18, 219, 255, 0.30), transparent 70%),
    radial-gradient(560px 560px at 90% 42%, rgba(186, 85, 254, 0.25), transparent 70%),
    radial-gradient(420px 420px at 28% 80%, rgba(35, 27, 255, 0.22), transparent 70%);
  background-repeat: no-repeat;
  animation: fl-bg-pan 40s ease-in-out infinite alternate;
}
.flutter-glow-top {
  position: absolute; left: 50%; top: -94px; transform: translateX(-50%);
  width: min(100vw, 1238px); height: 640px;
  background: radial-gradient(ellipse at center, rgba(35, 27, 255, 0.14), transparent 70%);
  pointer-events: none;
}
.flutter-glow-streaks {
  position: absolute; inset: 0; pointer-events: none; overflow: hidden;
}
.flutter-streak {
  position: absolute;
  width: var(--w); height: 842px;
  left: var(--l); top: var(--t);
  background: linear-gradient(180deg, rgba(var(--g), var(--o)), rgba(var(--g), 0));
  opacity: var(--op, 1); filter: blur(var(--b)); transform: rotate(25.32deg);
}
/* Sparse rising particles — the only element layer; the drifting orbs live
   inside .main-bg-gradient itself. z-index 0: above main's background,
   below the content wrapper (.relative z-10). */
.flutter-bg-live {
  position: fixed; inset: 0; z-index: 0;
  overflow: hidden; pointer-events: none;
}
.fl-particle {
  position: absolute; bottom: -12px;
  left: var(--x);
  width: var(--s); height: var(--s); border-radius: 50%;
  background: rgba(18, 219, 255, var(--o));
  box-shadow: 0 0 6px rgba(18, 219, 255, 0.5);
  opacity: 0;
  animation: fl-rise var(--d) linear infinite;
  animation-delay: var(--delay);
}
@keyframes fl-rise {
  0% { transform: translateY(0); opacity: 0; }
  8% { opacity: var(--o); }
  85% { opacity: var(--o); }
  100% { transform: translateY(-108vh); opacity: 0; }
}
/* Light theme: softer purple particles on the light gray base */
html:not(.my-app-dark) .fl-particle {
  background: rgba(124, 58, 237, var(--o));
  box-shadow: 0 0 8px rgba(124, 58, 237, 0.55);
}

/* Reduced-motion users: keep the slow background pan (subtle, non-vestibular),
   but stop the rising particles — discrete moving objects that trigger
   vestibular responses (global rule in critical.css). */
@media (prefers-reduced-motion: reduce) {
  .main-bg-gradient, .main-bg-gradient-light { animation-duration: 40s !important; animation-iteration-count: infinite !important; }
  .fl-particle { display: none; }
}

/* Mobile: halve the particle count to save composite cost on low-end GPUs */
@media (max-width: 767px) {
  .fl-particle:nth-child(even) { display: none; }
}

/* Uniform vertical rhythm: shared design components hardcode their own
   margins (my-16 / mt-24 mb-16 / lg:my-[100px] / md:mt-12 …), so without a
   page-level override the gaps between sections come out unequal. */
:deep(.flutter-content > *) {
  margin-top: 64px;
  margin-bottom: 0;
}
@media (min-width: 1024px) {
  :deep(.flutter-content > *) {
    margin-top: 100px;
  }
}
</style>

<style>
/* This page renders its own "webreinvent" section (WrFlutterMaskSection)
   on the page background, so hide the layout's shared copy while the
   Flutter page is active (class toggled in the page script). */
html.flutter-page #mask-section {
  display: none;
}

/* This page only: every WrButton (hero CTA, section CTAs) uses the hero
   button's blue-indigo gradient. Targets the link/button inside
   WrButton's p-px rounded-full wrapper; the 1px purple ring is kept.
   :not(:has(img)) skips the header's theme-toggle button, which keeps
   its own white styling in light mode. */

html.flutter-page div.rounded-full.p-px > button:not(:has(img)) {
  background-image: linear-gradient(95.99deg, #586BCC 3.69%, #23216C 96.65%);
}

</style>
